import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, node, number, shape, string } from 'prop-types'
import { PureComponent } from 'react'
import { compose } from 'redux'

import { CROSSSELL_ATTR_ANALYTICS_NAME } from 'shared/consts/crosssellWidgets'
import { isRebrandingActive } from 'shared/experiments/utils/rebranding'
import Button from 'views/components/atoms/Button'
import IconButtonArrowRight from 'views/components/atoms/Icon/IconButtonArrowRight'
import ButtonNavigation from 'views/components/molecules/ButtonNavigation'
import {
  MORE_PRODUCTS_XSELL_ID,
  VISIBLE_COUNT_DESKTOP_DEFAULT,
  VISIBLE_COUNT_MOBILE_DEFAULT,
  VISIBLE_COUNT_TABLET_DEFAULT,
} from 'views/consts'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { mountPointsType } from 'views/utils/propTypes'

import SliderHeader from './SliderHeader'
import SliderItemsList from './SliderItemsList'

import './SliderHorizontal.scss'

class SliderHorizontal extends PureComponent {
  render() {
    const {
      publicRuntimeConfig: {
        pageProperties: { experiments },
      },
      adaptStrategy,
      adaptableArrows,
      centered,
      className,
      customCssClass,
      headerClassName,
      headerTag,
      hideArrowsOnMobile,
      items,
      itemsForLazyLoad,
      mountPoints,
      moreLinkTitle,
      moreLinkUrl,
      navWithOpacity,
      noAutoFullWidth,
      noSlider,
      showBuyButton,
      stretch,
      target,
      thumbnailNav,
      title,
      titleIsHtml,
      visibleItemCount,
      visibleItemCountSmall,
      visibleItemCountTiny,
      wid,
      isSponsored,
      showMoreLinkUrl,
    } = this.props

    if (!items || !items.length) {
      return null
    }

    let moreLink = moreLinkUrl

    if (wid && showMoreLinkUrl) {
      const crossselProducts = items
        .map(({ props: { product } }) => product)
        .filter(Boolean)
      moreLink =
        moreLinkUrl || mountPoints.getSearchProductsList(crossselProducts)
    }

    const colCount =
      noSlider || items.length === 1
        ? 4
        : Math.min(items.length, visibleItemCount)

    const colSize = 12 / colCount
    const sliderClassName = classNames(
      'o-SliderHorizontal',
      className,
      customCssClass,
      {
        'o-SliderHorizontal__nav-with-opacity': navWithOpacity,
      }
    )

    const sliderProps = {
      className: sliderClassName,
      'data-crosssell-id': wid,
      'data-crosssell-title': title,
      'data-slider-adapt-strategy': adaptStrategy,
      'data-analytics': wid && CROSSSELL_ATTR_ANALYTICS_NAME,
    }

    if (!noSlider) {
      sliderProps['data-clientside-hook'] = thumbnailNav
        ? 'slider__thumbnails'
        : 'slider'
      sliderProps['data-slider-slideby'] = visibleItemCount

      if (visibleItemCountSmall) {
        sliderProps['data-slider-slideby-small'] = visibleItemCountSmall
      }

      if (visibleItemCountTiny) {
        sliderProps['data-slider-slideby-tiny'] = visibleItemCountTiny
      }
    }

    const navigationClassName = additionalClassName =>
      classNames(
        `o-SliderHorizontal__nav u-transform-center--y ${additionalClassName}`,
        {
          'u-hidden@small-tiny': hideArrowsOnMobile,
          'o-SliderHorizontal__nav--hidden': adaptableArrows,
        }
      )

    const isRebrandingEnabled = isRebrandingActive(experiments)

    return (
      <div {...sliderProps}>
        {(title || moreLink) && (
          <SliderHeader
            title={title}
            titleIsHtml={titleIsHtml}
            moreLinkTitle={moreLinkTitle}
            moreLinkUrl={moreLink}
            target={target}
            className={headerClassName}
            headerTag={headerTag}
            isSponsored={isSponsored}
            wid={wid}
          />
        )}
        {!noSlider && (
          <>
            <ButtonNavigation
              direction="left"
              iconSize="large"
              name="product-slicer-previous-button"
              data-clientside-hook={
                adaptableArrows
                  ? 'slider__prev adaptable__arrows'
                  : 'slider__prev'
              }
              isClientsideControlled
              className={navigationClassName('o-SliderHorizontal__previous')}
            >
              <Trans id="general.link.label.back" />
            </ButtonNavigation>
            <ButtonNavigation
              direction="right"
              iconSize="large"
              name="product-slider-next-button"
              data-clientside-hook={
                adaptableArrows
                  ? 'slider__next adaptable__arrows'
                  : 'slider__next'
              }
              isClientsideControlled
              className={navigationClassName('o-SliderHorizontal__next')}
            >
              <Trans id="general.link.label.forward" />
            </ButtonNavigation>
          </>
        )}
        <div
          className={classNames({
            'o-SliderHorizontal__wrapper': true,
            'o-SliderHorizontal__wrapper--noSlider': noSlider,
          })}
        >
          <SliderItemsList
            stretch={stretch}
            centered={centered}
            noSlider={noSlider}
            noAutoFullWidth={noAutoFullWidth}
            items={items}
            colSize={colSize}
            showBuyButton={showBuyButton}
          />
          {itemsForLazyLoad && (
            <div
              data-clientside-hook={MORE_PRODUCTS_XSELL_ID}
              data-items={JSON.stringify(itemsForLazyLoad)}
            />
          )}
        </div>
        {itemsForLazyLoad && (
          <div className="o-SliderHorizontal__show-more u-text-align--center u-padding--top">
            <Button
              reverse
              inlineDesktopOnly
              data-clientside-hook="showMoreButton"
              isClientsideControlled
              name="show-more-products-button"
            >
              <Trans id="general.label.loadMoreProducts" />
              {isRebrandingEnabled && (
                <IconButtonArrowRight className="a-icon-button-arrow-right" />
              )}
            </Button>
          </div>
        )}
      </div>
    )
  }
}

SliderHorizontal.propTypes = {
  ...SliderHeader.propTypes,
  adaptStrategy: string,
  adaptableArrows: bool,
  centered: bool,
  className: string,
  customCssClass: string,
  headerClassName: string,
  headerTag: string,
  hideArrowsOnMobile: bool,
  items: arrayOf(node),
  itemsForLazyLoad: arrayOf(shape({})),
  navWithOpacity: bool,
  mountPoints: mountPointsType.isRequired,
  noAutoFullWidth: bool,
  noSlider: bool,
  showBuyButton: bool,
  stretch: bool,
  target: string,
  thumbnailNav: bool,
  visibleItemCount: number,
  visibleItemCountSmall: number,
  visibleItemCountTiny: number,
  wid: number,
}

SliderHorizontal.defaultProps = {
  ...SliderHeader.defaultProps,
  adaptStrategy: null,
  adaptableArrows: false,
  centered: true,
  className: null,
  customCssClass: null,
  headerClassName: null,
  headerTag: 'h2',
  hideArrowsOnMobile: true,
  items: null,
  itemsForLazyLoad: null,
  navWithOpacity: true,
  noAutoFullWidth: false,
  noSlider: false,
  showBuyButton: false,
  stretch: false,
  target: null,
  thumbnailNav: null,
  visibleItemCount: VISIBLE_COUNT_DESKTOP_DEFAULT,
  visibleItemCountSmall: VISIBLE_COUNT_TABLET_DEFAULT,
  visibleItemCountTiny: VISIBLE_COUNT_MOBILE_DEFAULT,
  wid: null,
}

export default compose(
  withShopAndRuntimeConfig,
  withMountPoints
)(SliderHorizontal)
